import { useMemo, useState, useCallback } from "react";

import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid-pro";
import {
  CheckCircleIcon,
  ChipStatus,
  Link,
  Trash01Icon,
  XCircleIcon,
} from "components";
import { Paths, Permissions } from "configs";
import { confirm } from "contexts";
import { initLanContent } from "helpers";
import usePermission from "hooks/usePermission";
import useSyncStateWithUrl from "hooks/useSyncStateWithUrl";
import {
  useRoles,
  RoleListRequest,
  Role,
  useRoleDelete,
  useRoleUpdateStatus,
} from "services";
import { i18n } from "translations";
import { removeEmptyProperties } from "utils";
import { formatTime } from "utils/time";

export const useHook = () => {
  const { canAccess } = usePermission();
  const [keyword, setKeyword] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const {
    queryKey,
    state: filterState,
    updateState: setFilters,
    reset: resetFilters,
    debounceUpdateState: debounceSetFilters,
  } = useSyncStateWithUrl<RoleListRequest>(
    {
      name: undefined,
      code: undefined,
      description: undefined,
      status: undefined,
      page: 1,
      limit: 10,
    },
    {
      mode: "onChange",
      exclude: ["applicationId", "organizationId"],
      disabledLoadOnMount: true,
    }
  );

  const {
    data: rolesRes,
    isFetching,
    refetch,
  } = useRoles({
    queryKey: ["roleListHooks", queryKey],
    params: removeEmptyProperties(filterState),
    options: {
      enabled: !!filterState.applicationId && !!queryKey,
    },
  });

  const { mutate: deleteRole } = useRoleDelete({
    options: {
      onSuccess: () => {
        refetch();
      },
    },
  });
  const { mutate: updateStatusRole } = useRoleUpdateStatus({
    options: {
      onSuccess: () => {
        refetch();
      },
    },
  });

  const handleDeleteRole = useCallback(
    (id: number, applicationId: number) => {
      confirm({
        title: i18n.t("removeRole"),
        content: i18n.t("areYouSureYouWantToRemoveThisRole"),
      })
        .then(() => deleteRole({ id, applicationId }))
        .catch(() => {});
    },
    [deleteRole]
  );

  const rows = useMemo(
    () =>
      rolesRes?.data?.items?.map((item) => ({
        ...item,
        createdAt: formatTime(item?.createdAt),
        updatedAt: formatTime(item?.updatedAt),
        createdBy: item?.createdBy?.fullName,
        updatedBy: item?.updatedBy?.fullName,
      })) ?? [],
    [rolesRes?.data?.items]
  );

  const columns = useMemo((): GridColDef<Role>[] => {
    return [
      {
        field: "name",
        headerName: i18n.t("name"),
        valueGetter: (params) => params.row?.name,
        renderCell: (params) => (
          <Link to={`${Paths.RoleDetail}/${params?.row?.id}`}>
            {params.row?.name}
          </Link>
        ),
        width: 200,
      },
      {
        field: "organization",
        headerName: i18n.t("organization"),
        width: 200,
        valueGetter: (params) => params.row?.organization?.name || "",
      },
      {
        field: "description",
        headerName: i18n.t("description"),
        width: 200,
      },
      {
        field: "status",
        headerName: i18n.t("status"),
        renderCell: (params) => {
          const isActive = params?.row?.status === 1;
          return (
            <ChipStatus
              label={isActive ? i18n.t("active") : i18n.t("inactive")}
              type={isActive ? "success" : "error"}
            />
          );
        },
        width: 150,
      },
      {
        field: "createdAt",
        headerName: i18n.t("createdAt"),
        width: 180,
      },
      {
        field: "createdBy",
        headerName: i18n.t("createdBy"),
        width: 180,
      },
      {
        field: "updatedAt",
        headerName: i18n.t("updatedDate"),
        width: 180,
      },
      {
        field: "updatedBy",
        headerName: i18n.t("updatedBy"),
        width: 180,
      },
      {
        field: "actions",
        headerName: "",
        type: "actions",
        width: 110,
        getActions: (param) =>
          canAccess(Permissions.Role.Update)
            ? [
                canAccess(Permissions.Role.Active) ? (
                  <GridActionsCellItem
                    key={1}
                    icon={<CheckCircleIcon color="success" />}
                    label="Active"
                    onClick={() => {
                      confirm({
                        title: initLanContent([
                          "Bạn có chắc chắn",
                          "Are you sure?",
                        ]),
                        okLabel: initLanContent(["Đồng ý", "Confirm"]),
                        cancelLabel: initLanContent(["Hủy", "Cancel"]),
                      })
                        .then(() => {
                          updateStatusRole({
                            id: param.row.id,
                            isActive: true,
                            applicationId: param.row.applicationId,
                          });
                        })
                        .catch(() => {});
                    }}
                  />
                ) : (
                  <div />
                ),
                canAccess(Permissions.Role.Inactive) ? (
                  <GridActionsCellItem
                    key={2}
                    icon={<XCircleIcon color="error" />}
                    label="Inactive"
                    onClick={() => {
                      confirm({
                        title: initLanContent([
                          "Bạn có chắc chắn",
                          "Are you sure?",
                        ]),
                        okLabel: initLanContent(["Đồng ý", "Confirm"]),
                        cancelLabel: initLanContent(["Hủy", "Cancel"]),
                      })
                        .then(() => {
                          updateStatusRole({
                            id: param.row.id,
                            isActive: false,
                            applicationId: param.row.applicationId,
                          });
                        })
                        .catch(() => {});
                    }}
                  />
                ) : (
                  <div />
                ),
                canAccess(Permissions.Role.Delete) ? (
                  <GridActionsCellItem
                    key={3}
                    icon={<Trash01Icon color="error" />}
                    label="Delete"
                    onClick={() => {
                      confirm({
                        title: initLanContent([
                          "Bạn có chắc chắn",
                          "Are you sure?",
                        ]),
                        okLabel: initLanContent(["Đồng ý", "Confirm"]),
                        cancelLabel: initLanContent(["Hủy", "Cancel"]),
                      })
                        .then(() => {
                          handleDeleteRole(
                            param.row.id,
                            param.row.applicationId
                          );
                        })
                        .catch(() => {});
                    }}
                  />
                ) : (
                  <div />
                ),
              ]
            : [],
      },
    ];
  }, [canAccess, handleDeleteRole, updateStatusRole]);

  const handleClearFilter = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  return {
    keyword,
    selectedStatus,
    columns,
    setKeyword,
    setSelectedStatus,

    filter: {
      state: filterState,
      setFilters,
      debounceSetFilters,
      handleClearFilter,
    },
    dataTable: {
      isFetching,
      page: filterState.page ?? 1,
      limit: filterState.limit ?? 10,
      total: rolesRes?.data?.total ?? 0,
      columns,
      rows,
    },
  };
};
